import { getSupabase, getCurrentUser } from './supabase';
import type { Livestream } from './types';

/**
 * Get current or upcoming livestream
 */
export async function getCurrentLivestream(): Promise<Livestream | null> {
  const supabase = getSupabase();

  // First try to get a live stream
  const { data: liveStreams, error: liveError } = await supabase
    .from('livestreams')
    .select('*')
    .eq('status', 'live')
    .order('started_at', { ascending: false })
    .limit(1);

  // Ignore PGRST116 (no rows) - this is expected when there are no live streams
  if (liveError && liveError.code !== 'PGRST116') {
    console.error('Error fetching live stream:', liveError);
  }

  if (liveStreams && liveStreams.length > 0) return liveStreams[0];

  // If no live stream, get the next scheduled one
  const { data: scheduledStreams, error: scheduledError } = await supabase
    .from('livestreams')
    .select('*')
    .eq('status', 'scheduled')
    .gte('scheduled_for', new Date().toISOString())
    .order('scheduled_for', { ascending: true })
    .limit(1);

  // Ignore PGRST116 (no rows) - this is expected when there are no scheduled streams
  if (scheduledError && scheduledError.code !== 'PGRST116') {
    console.error('Error fetching scheduled stream:', scheduledError);
  }

  return (scheduledStreams && scheduledStreams.length > 0) ? scheduledStreams[0] : null;
}

/**
 * Get archived livestreams
 */
export async function getArchivedLivestreams(): Promise<Livestream[]> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('livestreams')
    .select('*')
    .in('status', ['ended', 'archived'])
    .order('ended_at', { ascending: false });

  if (error) {
    console.error('Error fetching archived streams:', error);
    return [];
  }

  return data || [];
}

/**
 * Track that user viewed a livestream
 */
export async function trackLivestreamView(livestreamId: string): Promise<void> {
  const supabase = getSupabase();
  const user = await getCurrentUser();

  if (!user) return;

  await supabase
    .from('livestream_views')
    .upsert({
      livestream_id: livestreamId,
      user_id: user.id,
      viewed_at: new Date().toISOString(),
    }, {
      onConflict: 'livestream_id,user_id'
    });
}

/**
 * Subscribe to livestream status changes (realtime)
 */
export function subscribeLivestreamUpdates(
  callback: (livestream: Livestream) => void
): () => void {
  const supabase = getSupabase();

  const channel = supabase
    .channel('livestream-updates')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'livestreams',
      },
      (payload) => {
        callback(payload.new as Livestream);
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    channel.unsubscribe();
  };
}
